// ============================================================
// CLIENT ACCESS TAB + ONBOARDING ANSWERS
//
// AccessTab              what access we have, what we still need, logins, notes
// OnboardingAnswersCard  built-in "document" showing every onboarding answer
//
// Passwords are never stored or read by this app directly. They go through
// the onboarding app's server, which encrypts them with a key that lives
// only in Vercel. The database holds unreadable ciphertext.
// ============================================================
import { useState, useEffect, useCallback } from 'react'
import { Plus, Copy, Eye, EyeOff, RefreshCw, Link2, X, ExternalLink, KeyRound, FileText, Trash2, CheckCircle } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import {
  ACCESS_PLATFORMS, ONBOARDING_BASE_URL, platformsForServices,
  generateOnboardingToken, onboardingUrlFor,
} from './onboardingConfig'

const STATUSES = [
  { value: 'not_started',      label: 'Not started',            bg: '#fff',    color: '#4a5a72' },
  { value: 'client_confirmed', label: 'Checking / pending',     bg: '#e8f5fa', color: '#1a2b4a' },
  { value: 'verified',         label: 'Connected',              bg: '#eaf7f0', color: '#2e6e3a' },
  { value: 'not_found',        label: "We don't see it",        bg: '#f7e9f2', color: '#8c4d78' },
  { value: 'needs_help',       label: 'Client needs help',      bg: '#f0eef6', color: '#1a2b4a' },
  { value: 'no_account',       label: "Client doesn't have it", bg: '#f5ede6', color: '#4a5a72' },
]
const statusMeta = v => STATUSES.find(s => s.value === v) || STATUSES[0]
const HAVE = ['verified']
const NOT_NEEDED = ['no_account']

const label11 = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }

async function copyText(text, toast, what = 'Copied') {
  try { await navigator.clipboard.writeText(text); toast(what, 'success') } catch { toast('Could not copy', 'error') }
}

// ── Password vault calls (onboarding app server) ─────────────
async function secretCall(action, itemId, value) {
  const { data } = await supabase.auth.getSession()
  const jwt = data?.session?.access_token
  if (!jwt) return { ok: false, error: 'Your session expired. Sign in again.' }
  try {
    const res = await fetch(`${ONBOARDING_BASE_URL}/api/team/secret`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ action, itemId, value }),
    })
    return await res.json()
  } catch {
    return { ok: false, error: 'Could not reach the onboarding app. Check REACT_APP_ONBOARDING_URL and TASK_APP_ORIGIN.' }
  }
}

// ════════════════════════════════════════════════════════════
// Onboarding link box: create, copy, regenerate, revoke
// ════════════════════════════════════════════════════════════
function OnboardingLinkBox({ client, currentUser, link, onChanged }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const url = link ? onboardingUrlFor(link.token) : ''

  const create = async (revokeOld) => {
    if (revokeOld && !window.confirm('The old link will stop working right away. The client keeps all their saved answers. Continue?')) return
    setBusy(true)
    if (revokeOld && link) {
      await supabase.from('onboarding_links').update({ revoked_at: new Date().toISOString() }).eq('id', link.id)
    }
    const token = generateOnboardingToken()
    const { error } = await supabase.from('onboarding_links').insert({ client_id: client.id, token, created_by: currentUser?.id || null })
    if (error) { toast('Error: ' + error.message, 'error'); setBusy(false); return }
    await supabase.from('clients').update({ info_grab_link: onboardingUrlFor(token) }).eq('id', client.id)
    toast(revokeOld ? 'New link created, old one turned off' : 'Onboarding link created', 'success')
    setBusy(false)
    onChanged()
  }

  const revoke = async () => {
    if (!window.confirm('Turn this link off? The client will see "this link isn\'t working" until you create a new one.')) return
    setBusy(true)
    await supabase.from('onboarding_links').update({ revoked_at: new Date().toISOString() }).eq('id', link.id)
    toast('Link turned off', 'success')
    setBusy(false)
    onChanged()
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header"><span className="card-title">🔗 Client onboarding link</span></div>
      <div className="card-body">
        {link ? (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ flex: 1, minWidth: 220, fontSize: 12, background: 'var(--cream)', padding: '8px 10px', borderRadius: 6, overflowWrap: 'anywhere' }}>{url}</code>
              <button className="btn btn-gold btn-sm" onClick={() => copyText(url, toast, 'Link copied')}><Copy size={12} /> Copy</button>
              <a className="btn btn-ghost btn-sm" href={`${url}/summary`} target="_blank" rel="noopener noreferrer"><ExternalLink size={12} /> Printable answers</a>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              {link.completed_at ? `Client marked finished ${new Date(link.completed_at).toLocaleDateString()}.` : 'Client has not marked it finished yet.'}
              {' '}The same link keeps working if you add accounts below, so you can send it again after a plan upgrade.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => create(true)}><RefreshCw size={12} /> New link (turns off old)</button>
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={revoke}><X size={12} /> Turn link off</button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No active onboarding link for this client.</span>
            <button className="btn btn-gold btn-sm" disabled={busy} onClick={() => create(false)}><Link2 size={12} /> Create onboarding link</button>
          </div>
        )}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// One account row
// ════════════════════════════════════════════════════════════
function AccessRow({ item, onSaved }) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [f, setF] = useState({
    status: item.status, enabled: item.enabled,
    identifier: item.identifier || '', access_holder: item.access_holder || '',
    login_username: item.login_username || '', notes: item.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [pw, setPw] = useState('')
  const [revealed, setRevealed] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const meta = statusMeta(item.status)
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }))

  const save = async () => {
    setSaving(true)
    const update = {
      status: f.status, enabled: f.enabled,
      identifier: f.identifier.trim() || null, access_holder: f.access_holder.trim() || null,
      login_username: f.login_username.trim() || null, notes: f.notes.trim() || null,
      updated_at: new Date().toISOString(),
    }
    if (f.status === 'verified' && item.status !== 'verified') update.verified_at = new Date().toISOString()
    const { error } = await supabase.from('client_access_items').update(update).eq('id', item.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Saved', 'success')
    onSaved()
  }

  const savePw = async () => {
    if (!pw) return
    setPwBusy(true)
    const res = await secretCall('set', item.id, pw)
    setPwBusy(false)
    if (!res.ok) { toast(res.error || 'Could not save password', 'error'); return }
    setPw(''); setRevealed('')
    toast('Password encrypted and saved', 'success')
    onSaved()
  }
  const reveal = async () => {
    if (revealed) { setRevealed(''); return }
    setPwBusy(true)
    const res = await secretCall('reveal', item.id)
    setPwBusy(false)
    if (!res.ok) { toast(res.error || 'Could not load password', 'error'); return }
    setRevealed(res.value)
    // Don't leave it on screen.
    setTimeout(() => setRevealed(''), 30000)
  }
  const clearPw = async () => {
    if (!window.confirm('Delete the saved password for this account?')) return
    setPwBusy(true)
    const res = await secretCall('clear', item.id)
    setPwBusy(false)
    if (!res.ok) { toast(res.error || 'Could not delete', 'error'); return }
    setRevealed('')
    toast('Password deleted', 'success')
    onSaved()
  }

  return (
    <div className="card" style={{ padding: 0, opacity: item.enabled ? 1 : 0.55 }}>
      <div className="card-body" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>{ACCESS_PLATFORMS[item.platform] || item.platform}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[item.identifier, item.access_holder && `Access: ${item.access_holder}`, item.has_secret && 'Password saved', !item.enabled && 'Not needed'].filter(Boolean).join('  |  ') || 'No details yet'}
          </div>
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: meta.bg, color: meta.color, border: '1px solid var(--border-light)', whiteSpace: 'nowrap' }}>{meta.label}</span>
      </div>

      {open && (
        <div style={{ padding: '4px 16px 16px', borderTop: '1px solid var(--border-light)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Status</label>
              <select className="form-control" value={f.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Account ID, URL, or platform</label>
              <input className="form-control" value={f.identifier} onChange={e => set('identifier', e.target.value)} placeholder="e.g. 123-456-7890, GoDaddy, @handle" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Which LMR account has access</label>
              <input className="form-control" value={f.access_holder} onChange={e => set('access_holder', e.target.value)} placeholder="e.g. lparker@, jade@" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Login username (if we sign in as them)</label>
              <input className="form-control" value={f.login_username} onChange={e => set('login_username', e.target.value)} autoComplete="off" />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label"><KeyRound size={11} /> Password (encrypted)</label>
            {item.has_secret && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                <code style={{ fontSize: 13, background: 'var(--cream)', padding: '6px 10px', borderRadius: 6, minWidth: 140 }}>{revealed || '••••••••••'}</code>
                <button className="btn btn-ghost btn-sm" disabled={pwBusy} onClick={reveal}>{revealed ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Reveal</>}</button>
                {revealed && <button className="btn btn-ghost btn-sm" onClick={() => copyText(revealed, toast, 'Password copied')}><Copy size={12} /> Copy</button>}
                <button className="btn btn-ghost btn-sm" disabled={pwBusy} onClick={clearPw}><Trash2 size={12} /> Delete</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-control" type="password" autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)} placeholder={item.has_secret ? 'Replace with a new password' : 'Enter password to save'} />
              <button className="btn btn-ghost btn-sm" disabled={pwBusy || !pw} onClick={savePw}>Save password</button>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>Every reveal is logged with your name. Hides itself after 30 seconds.</div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="2FA goes to the owner's phone, text before logging in. Domain renews in March. Etc." />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={f.enabled} onChange={e => set('enabled', e.target.checked)} />
              We need this from the client (uncheck to hide it from their page)
            </label>
            <button className="btn btn-gold btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// Access tab
// ════════════════════════════════════════════════════════════
export function AccessTab({ client, currentUser }) {
  const toast = useToast()
  const [items, setItems] = useState([])
  const [link, setLink] = useState(null)
  const [loading, setLoading] = useState(true)
  const [addKey, setAddKey] = useState('')

  const load = useCallback(async () => {
    const [{ data: its }, { data: links }] = await Promise.all([
      supabase.from('client_access_items').select('*').eq('client_id', client.id),
      supabase.from('onboarding_links').select('*').eq('client_id', client.id).is('revoked_at', null).order('created_at', { ascending: false }).limit(1),
    ])
    const order = Object.keys(ACCESS_PLATFORMS)
    setItems((its || []).sort((a, b) => order.indexOf(a.platform) - order.indexOf(b.platform)))
    setLink((links || [])[0] || null)
    setLoading(false)
  }, [client.id])
  useEffect(() => { load() }, [load])

  const existing = items.map(i => i.platform)
  const missingForServices = platformsForServices(client).filter(p => !existing.includes(p))
  const addable = Object.keys(ACCESS_PLATFORMS).filter(p => !existing.includes(p))

  const addPlatforms = async (platforms) => {
    if (platforms.length === 0) return
    const { error } = await supabase.from('client_access_items').insert(platforms.map(platform => ({ client_id: client.id, platform })))
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast(platforms.length === 1 ? 'Account added' : `${platforms.length} accounts added`, 'success')
    setAddKey('')
    load()
  }

  const active = items.filter(i => i.enabled && !NOT_NEEDED.includes(i.status))
  const have = active.filter(i => HAVE.includes(i.status))
  const need = active.filter(i => !HAVE.includes(i.status))

  if (loading) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading…</div>

  return (
    <div>
      <OnboardingLinkBox client={client} currentUser={currentUser} link={link} onChanged={load} />

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div>
            <div style={label11}>Connected ({have.length})</div>
            <div style={{ fontSize: 13 }}>{have.length ? have.map(i => ACCESS_PLATFORMS[i.platform] || i.platform).join(', ') : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Nothing confirmed yet</span>}</div>
          </div>
          <div>
            <div style={label11}>Still need ({need.length})</div>
            <div style={{ fontSize: 13 }}>{need.length ? need.map(i => `${ACCESS_PLATFORMS[i.platform] || i.platform} (${statusMeta(i.status).label.toLowerCase()})`).join(', ') : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>All set</span>}</div>
          </div>
        </div>
      </div>

      {missingForServices.length > 0 && (
        <div className="card" style={{ marginBottom: 16, background: 'var(--cream)' }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, fontSize: 13 }}>
              Based on this client's services, we're not tracking: <strong>{missingForServices.map(p => ACCESS_PLATFORMS[p]).join(', ')}</strong>
            </span>
            <button className="btn btn-gold btn-sm" onClick={() => addPlatforms(missingForServices)}><Plus size={12} /> Add these</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(item => <AccessRow key={item.id + item.updated_at} item={item} onSaved={load} />)}
        {items.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', padding: 8 }}>No accounts tracked yet.</div>}
      </div>

      {addable.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
          <select className="form-control" style={{ maxWidth: 320 }} value={addKey} onChange={e => setAddKey(e.target.value)}>
            <option value="">Add another account…</option>
            {addable.map(p => <option key={p} value={p}>{ACCESS_PLATFORMS[p]}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" disabled={!addKey} onClick={() => addPlatforms([addKey])}><Plus size={12} /> Add</button>
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// Onboarding answers: every response in one list
// ════════════════════════════════════════════════════════════
const SECTION_TITLES = { business: 'Business', meetings: 'Meeting times', brand: 'Brand', website: 'Website and advertising' }
const KEY_LABELS = {
  description: 'What they do', process_notes: 'Process notes',
  timezone: 'Time zone', cadence: 'How often', slots: 'Available times',
  colors_feeling: 'Feeling about colors', fonts_feeling: 'Feeling about fonts', socials: 'Social profiles',
  regions: 'Areas served', competitors: 'Competitors', services: 'Services to be found for',
  ratings: 'Website ratings', redesign_notes: 'Redesign notes',
  design: 'Overall design', ease: 'Ease of use', accuracy: 'Accurate and up to date', voice: 'Voice and values', interactive: 'Forms and interactive parts',
}
const pretty = k => KEY_LABELS[k] || k.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())

// Renders any shape, so new questions show up here without code changes.
function Value({ v }) {
  if (v === null || v === undefined || v === '') return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not answered</span>
  if (Array.isArray(v)) {
    if (v.length === 0) return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not answered</span>
    if (v.every(x => typeof x !== 'object')) return <span>{v.join(', ')}</span>
    return <div>{v.map((x, i) => <div key={i}>{Object.values(x).filter(Boolean).join('  |  ')}</div>)}</div>
  }
  if (typeof v === 'object') {
    const entries = Object.entries(v).filter(([, val]) => val !== '' && val !== null && val !== undefined)
    if (entries.length === 0) return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not answered</span>
    return <div>{entries.map(([k, val]) => <div key={k}><strong>{pretty(k)}:</strong> {String(val)}</div>)}</div>
  }
  return <span style={{ whiteSpace: 'pre-wrap' }}>{String(v)}</span>
}

function AnswersModal({ client, onClose }) {
  const [rows, setRows] = useState(null)
  const [items, setItems] = useState([])
  useEffect(() => {
    Promise.all([
      supabase.from('onboarding_responses').select('section, answers, updated_at').eq('client_id', client.id),
      supabase.from('client_access_items').select('platform, status, identifier, has_secret, enabled').eq('client_id', client.id),
    ]).then(([r, a]) => { setRows(r.data || []); setItems(a.data || []) })
  }, [client.id])

  const bySection = {}
  ;(rows || []).forEach(r => { bySection[r.section] = r })
  const sections = Object.keys(SECTION_TITLES).concat(Object.keys(bySection).filter(k => !SECTION_TITLES[k] && k !== 'progress'))
  const colors = Array.isArray(client.brand_colors) ? client.brand_colors : []
  const fonts = Array.isArray(client.brand_fonts) ? client.brand_fonts : []

  const profileRows = [
    ['Website', client.website], ['Location', client.company_location], ['Website built on', client.hosting_platform],
    ['Primary contact', [client.primary_contact_name, client.primary_contact_position].filter(Boolean).join(', ')],
    ['Email', client.contact_email], ['Cell', client.primary_contact_cell],
    ['Brand colors', colors.join(', ')], ['Fonts', fonts.join(', ')],
  ]

  const Row = ({ k, children }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '190px 1fr', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
      <div style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{k}</div><div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  )
  const H = ({ children }) => <div style={{ ...label11, marginTop: 20, marginBottom: 2, color: 'var(--navy)' }}>{children}</div>

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 760 }}>
        <div className="modal-header">
          <span className="modal-title">📋 Onboarding answers: {client.name}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="modal-body">
          {rows === null ? <div style={{ color: 'var(--text-muted)' }}>Loading…</div> : (
            <>
              {rows.filter(r => r.section !== 'progress').length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 8 }}>The client hasn't answered anything yet. Profile fields are shown below.</div>
              )}
              <H>Saved to the profile</H>
              {profileRows.map(([k, v]) => <Row key={k} k={k}><Value v={v} /></Row>)}

              {sections.map(sec => {
                const answers = bySection[sec]?.answers || {}
                const keys = Object.keys(answers)
                return (
                  <div key={sec}>
                    <H>{SECTION_TITLES[sec] || pretty(sec)}{bySection[sec]?.updated_at ? `  (updated ${new Date(bySection[sec].updated_at).toLocaleDateString()})` : ''}</H>
                    {keys.length === 0
                      ? <Row k="Answers"><Value v="" /></Row>
                      : keys.map(k => <Row key={k} k={pretty(k)}><Value v={answers[k]} /></Row>)}
                  </div>
                )
              })}

              <H>Account access</H>
              {items.length === 0 ? <Row k="Accounts"><Value v="" /></Row> : items.map(i => (
                <Row key={i.platform} k={ACCESS_PLATFORMS[i.platform] || i.platform}>
                  {statusMeta(i.status).label}{i.identifier ? `  |  ${i.identifier}` : ''}{i.has_secret ? '  |  password saved' : ''}{!i.enabled ? '  |  not needed' : ''}
                </Row>
              ))}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

/**
 * Shown inside a task that the onboarding page created (confirm access, set
 * up an account, help a client). Marks the access item Connected right from
 * the task, which updates the client's Access tab and their portal too.
 */
export function AccessConfirmPanel({ task, onDone }) {
  const toast = useToast()
  const itemId = task.triggered_by?.access_item_id
  const [item, setItem] = useState(null)
  const [identifier, setIdentifier] = useState('')
  const [holder, setHolder] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!itemId) return
    supabase.from('client_access_items').select('*').eq('id', itemId).maybeSingle().then(({ data }) => {
      setItem(data || null); setIdentifier(data?.identifier || ''); setHolder(data?.access_holder || '')
    })
  }, [itemId])
  if (!itemId || !item) return null

  const meta = statusMeta(item.status)
  const isSetup = task.triggered_by?.source === 'onboarding_no_account'

  const confirm = async () => {
    if (isSetup && !identifier.trim()) { toast('Enter the account ID / URL you created first', 'error'); return }
    setBusy(true)
    const { error } = await supabase.from('client_access_items').update({
      status: 'verified', verified_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      identifier: identifier.trim() || null, access_holder: holder.trim() || null,
    }).eq('id', itemId)
    setBusy(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    setItem({ ...item, status: 'verified' })
    toast('Marked Connected. Client portal and Access tab updated.', 'success')
    onDone && onDone()
  }

  return (
    <div style={{ marginBottom: 20, padding: '14px 16px', background: 'var(--cream)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--mauve-light)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          <KeyRound size={11} /> Access: {ACCESS_PLATFORMS[item.platform] || item.platform}
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: meta.bg, color: meta.color, border: '1px solid var(--border-light)' }}>{meta.label}</span>
      </div>
      {item.status === 'verified' ? (
        <div style={{ fontSize: 13 }}>Connected{item.verified_at ? ` on ${new Date(item.verified_at).toLocaleDateString()}` : ''}.</div>
      ) : (
        <>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            {isSetup ? 'Once you have created the account and added LMR, enter the ID and confirm.' : 'Check that LMR actually has access, then confirm. This marks it Connected for the client too.'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <input className="form-control" value={identifier} onChange={e => setIdentifier(e.target.value)} placeholder="Account ID / URL / handle" />
            <input className="form-control" value={holder} onChange={e => setHolder(e.target.value)} placeholder="Which LMR email has access" />
          </div>
          <button className="btn btn-gold btn-sm" disabled={busy} onClick={confirm}><CheckCircle size={12} /> Confirm access and complete task</button>
        </>
      )}
    </div>
  )
}

/** Built-in entry at the top of the Documents tab. Always there, always current. */
export function OnboardingAnswersCard({ client }) {
  const [show, setShow] = useState(false)
  return (
    <>
      <div className="card" style={{ padding: 0, marginBottom: 8, borderColor: 'var(--mauve-light)' }}>
        <div className="card-body" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <FileText size={18} style={{ color: 'var(--mauve)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>Onboarding Answers</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Everything the client submitted on their onboarding page, in one list. Always current.</div>
          </div>
          <button className="btn btn-gold btn-sm" onClick={() => setShow(true)}>View</button>
        </div>
      </div>
      {show && <AnswersModal client={client} onClose={() => setShow(false)} />}
    </>
  )
}
